import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Shield,
  MapPinned,
  Compass,
  User,
  LogOut,
  X,
  Globe,
  CheckCircle,
  Clock,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCategories } from "../../contexts/CategoryContext";
import { api } from "../../services/api";
import toast from "react-hot-toast";
import { REGION_OPTIONS } from "../auth/Register";

const REGION_TO_ASTRIA_CATEGORY: Record<string, string> = {
  indonesia: "Astria Indonesia",
  canada: "Astria Canada",
  uk: "Astria UK",
  gcc: "Astria GCC",
  malaysia: "Astria Malaysia",
  singapore: "Astria Singapore",
  philippines: "Astria Philippines",
  vietnam: "Astria Vietnam V2",
  mexico: "Astria Mexico V2",
  brazil: "Astria Brazil",
  korea: "Astria Korea",
  india: "Astria India",
  japan: "Astria Japan",
  spanish: "Astria Spanish",
  us: "Astria US",
};

const SIDEBAR_COLORS = {
  amethyst: "#6D4C92",
  darkAmethyst: "#2D1B44",
  roseGold: "#E5B8AD",
  pearl: "#F8F4FF",
};

const navItems = [
  { label: "astriaHome.nav.home", to: "/", icon: Home },
  { label: "astriaHome.nav.life", to: "/lifeGps", icon: Compass },
  { label: "astriaHome.nav.sanctuary", to: "/sanctuary", icon: Shield },
  { label: "astriaHome.nav.journey", to: "/journey", icon: MapPinned },
];

export default function Footer() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { user, logout, updateCurrentUser } = useAuth();
  const { categories } = useCategories();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    username: "",
    email: "",
    mobileNo: "",
    dob: "",
  });
  const [selectedRegion, setSelectedRegion] = useState(
    user?.region || "healjai",
  );
  const [isSavingRegion, setIsSavingRegion] = useState(false);
  const isTester = user?.roleId === 3;

  // Auto-format date as user types (DD/MM/YYYY)
  const formatDate = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");

    // Format as DD/MM/YYYY
    if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 4) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    } else if (digits.length <= 8) {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    }

    // Max 8 digits (DDMMYYYY)
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  };

  const profile = useMemo(() => {
    const safeUser: any = user;
    let storedUser: any = null;
    let storedToken: string | null = null;
    try {
      storedUser = JSON.parse(localStorage.getItem("user") || "null");
      storedToken = localStorage.getItem("token");
    } catch (e) {
      storedUser = null;
      storedToken = null;
    }

    const username = safeUser?.username ?? storedUser?.username ?? "";
    const email = safeUser?.email ?? storedUser?.email ?? "";
    const mobileNo =
      safeUser?.mobileNo ??
      safeUser?.mobile ??
      storedUser?.mobileNo ??
      storedUser?.mobile ??
      "";

    const dob = safeUser?.dob ?? storedUser?.dob ?? "";

    const userId = safeUser?._id ?? storedUser?._id ?? "";
    const canLogout = Boolean(safeUser?._id || storedUser?._id || storedToken);

    return { userId, username, email, mobileNo, dob, canLogout };
  }, [user]);

  useEffect(() => {
    setIsProfileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isProfileOpen) return;
    setIsEditingProfile(false);
    setIsSavingProfile(false);
    setProfileForm({
      username: profile.username || "",
      email: profile.email || "",
      mobileNo: profile.mobileNo || "",
      dob: profile.dob || "",
    });
    setSelectedRegion(user?.region || "healjai");
  }, [
    isProfileOpen,
    profile.username,
    profile.email,
    profile.mobileNo,
    user?.region,
  ]);

  const handleLogout = () => {
    logout();
    setIsProfileOpen(false);
  };

  const handleSaveProfile = async () => {
    if (isTester) {
      toast.error("Profile is locked for tester accounts");
      return;
    }
    if (!profile.userId) {
      toast.error("User not found");
      return;
    }

    setIsSavingProfile(true);
    try {
      const res = await api.put(`/backend/user/${profile.userId}`, {
        username: profileForm.username?.trim(),
        email: profileForm.email?.trim(),
        mobileNo: profileForm.mobileNo?.trim(),
        dob: profileForm.dob?.trim(),
      });

      const updated: any = res?.data || {};
      updateCurrentUser({
        _id: updated._id || profile.userId,
        username: updated.username ?? profileForm.username?.trim(),
        email: updated.email ?? profileForm.email?.trim(),
        mobileNo: updated.mobileNo ?? profileForm.mobileNo?.trim(),
        dob: updated.dob ?? profileForm.dob?.trim(),
      });

      toast.success("Profile updated");
      setIsEditingProfile(false);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update profile";
      toast.error(msg);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleSaveRegion = async () => {
    if (isTester) {
      toast.error("Region is locked for tester accounts");
      return;
    }
    if (!profile.userId) return;
    const currentRegion = user?.region || "healjai";
    if (selectedRegion === currentRegion) return;

    setIsSavingRegion(true);
    try {
      const res = await api.put(`/backend/user/${profile.userId}`, {
        region: selectedRegion,
      });
      const updated = res.data?.updatedUser || {};
      updateCurrentUser({
        region: updated.region || selectedRegion,
        allRegionsPending: updated.allRegionsPending || false,
        allRegionsApproved: updated.allRegionsApproved || false,
      });

      if (selectedRegion === "all") {
        toast.success("All Regions access requested. Awaiting admin approval.");
      } else {
        const label =
          REGION_OPTIONS.find((o) => o.value === selectedRegion)?.label ||
          selectedRegion;
        toast.success(`Region updated to ${label}`);

        // Redirect: Energy Match for Thailand, Compatibility for others
        setIsProfileOpen(false);
        navigate("/");
        // if (selectedRegion === "healjai") {
        //   const energyCat = categories.find(
        //     (c: any) => c.name === "Energy Match",
        //   );
        //   if (energyCat) navigate(`/energyMatch?category=${energyCat.id}`);
        // } else {
        //   const astriaCatName = REGION_TO_ASTRIA_CATEGORY[selectedRegion];
        //   const astriaCat = astriaCatName
        //     ? categories.find((c: any) => c.name === astriaCatName)
        //     : null;
        //   if (astriaCat) {
        //     navigate(
        //       `/energyMatch?country=${encodeURIComponent(astriaCatName)}&categoryId=${astriaCat.id}`,
        //     );
        //   }
        // }
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to update region");
    } finally {
      setIsSavingRegion(false);
    }
  };

  return (
    <>
      {isProfileOpen && (
        <div
          className="fixed inset-0 z-50"
          role="dialog"
          aria-modal="true"
          onClick={() => setIsProfileOpen(false)}
        >
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div
            className="fixed left-1/2 -translate-x-1/2 bottom-[20px] w-[92vw] max-w-sm rounded-3xl border shadow-xl p-4"
            style={{
              backgroundColor: SIDEBAR_COLORS.pearl,
              borderColor: `${SIDEBAR_COLORS.roseGold}55`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className="h-9 w-9 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: `${SIDEBAR_COLORS.roseGold}55` }}
                >
                  <User size={18} style={{ color: SIDEBAR_COLORS.amethyst }} />
                </div>
                <div className="leading-tight">
                  <div
                    className="text-sm font-semibold"
                    style={{ color: SIDEBAR_COLORS.darkAmethyst }}
                  >
                    {isTester
                      ? t("astriaHome.nav.testerMode")
                      : t("astriaHome.nav.profile")}
                  </div>
                  <div
                    className="text-[11px]"
                    style={{ color: `${SIDEBAR_COLORS.darkAmethyst}99` }}
                  >
                    {isTester
                      ? "Profile locked for testing"
                      : "Account details"}
                  </div>
                </div>
              </div>

              <button
                onClick={() => setIsProfileOpen(false)}
                className="h-9 w-9 rounded-2xl flex items-center justify-center transition"
                style={{ backgroundColor: `${SIDEBAR_COLORS.roseGold}33` }}
                aria-label="Close profile"
              >
                <X size={16} style={{ color: SIDEBAR_COLORS.darkAmethyst }} />
              </button>
            </div>

            <div className="mt-3 space-y-2">
              <div
                className="rounded-2xl border p-3"
                style={{ borderColor: `${SIDEBAR_COLORS.roseGold}40` }}
              >
                <div
                  className="text-[10px] font-medium uppercase tracking-wide"
                  style={{ color: `${SIDEBAR_COLORS.darkAmethyst}99` }}
                >
                  Username
                </div>
                {isEditingProfile ? (
                  <input
                    value={profileForm.username}
                    onChange={(e) =>
                      setProfileForm((p) => ({
                        ...p,
                        username: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 text-sm outline-none focus:border-black/30 bg-white"
                    placeholder="Enter username"
                  />
                ) : (
                  <div
                    className="text-sm font-semibold break-words"
                    style={{ color: SIDEBAR_COLORS.darkAmethyst }}
                  >
                    {profile.username || "—"}
                  </div>
                )}
              </div>

              <div
                className="rounded-2xl border p-3"
                style={{ borderColor: `${SIDEBAR_COLORS.roseGold}40` }}
              >
                <div
                  className="text-[10px] font-medium uppercase tracking-wide"
                  style={{ color: `${SIDEBAR_COLORS.darkAmethyst}99` }}
                >
                  Email
                </div>
                {isEditingProfile ? (
                  <input
                    value={profileForm.email}
                    onChange={(e) =>
                      setProfileForm((p) => ({ ...p, email: e.target.value }))
                    }
                    className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 text-sm outline-none focus:border-black/30 bg-white"
                    placeholder="Enter email"
                  />
                ) : (
                  <div
                    className="text-sm font-semibold break-words"
                    style={{ color: SIDEBAR_COLORS.darkAmethyst }}
                  >
                    {profile.email || "—"}
                  </div>
                )}
              </div>

              <div
                className="rounded-2xl border p-3"
                style={{ borderColor: `${SIDEBAR_COLORS.roseGold}40` }}
              >
                <div
                  className="text-[10px] font-medium uppercase tracking-wide"
                  style={{ color: `${SIDEBAR_COLORS.darkAmethyst}99` }}
                >
                  Mobile No
                </div>
                {isEditingProfile ? (
                  <input
                    value={profileForm.mobileNo}
                    onChange={(e) =>
                      setProfileForm((p) => ({
                        ...p,
                        mobileNo: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 text-sm outline-none focus:border-black/30 bg-white"
                    placeholder="Enter mobile number"
                  />
                ) : (
                  <div
                    className="text-sm font-semibold break-words"
                    style={{ color: SIDEBAR_COLORS.darkAmethyst }}
                  >
                    {profile.mobileNo || "—"}
                  </div>
                )}
              </div>

              <div
                className="rounded-2xl border p-3"
                style={{ borderColor: `${SIDEBAR_COLORS.roseGold}40` }}
              >
                <div
                  className="text-[10px] font-medium uppercase tracking-wide"
                  style={{ color: `${SIDEBAR_COLORS.darkAmethyst}99` }}
                >
                  Date of Birth
                </div>
                {isEditingProfile ? (
                  <input
                    type="text"
                    value={profileForm.dob}
                    onChange={(e) =>
                      setProfileForm((p) => ({
                        ...p,
                        dob: formatDate(e.target.value),
                      }))
                    }
                    className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 text-sm outline-none focus:border-black/30 bg-white"
                    placeholder="DD/MM/YYYY"
                    maxLength={10}
                  />
                ) : (
                  <div
                    className="text-sm font-semibold break-words"
                    style={{ color: SIDEBAR_COLORS.darkAmethyst }}
                  >
                    {profile.dob || "—"}
                  </div>
                )}
              </div>

              {/* Region selector */}
              <div
                className="rounded-2xl border p-3"
                style={{ borderColor: `${SIDEBAR_COLORS.roseGold}40` }}
              >
                <div
                  className="text-[10px] font-medium uppercase tracking-wide"
                  style={{ color: `${SIDEBAR_COLORS.darkAmethyst}99` }}
                >
                  Region
                </div>
                {isTester ? (
                  <div
                    className="mt-1 text-sm font-semibold"
                    style={{ color: SIDEBAR_COLORS.darkAmethyst }}
                  >
                    {user?.region || "—"}
                  </div>
                ) : (
                  <>
                    <select
                      value={selectedRegion}
                      onChange={(e) => setSelectedRegion(e.target.value)}
                      className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 text-sm outline-none focus:border-black/30 bg-white appearance-none"
                    >
                      {REGION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {selectedRegion !== (user?.region || "healjai") && (
                      <button
                        onClick={handleSaveRegion}
                        disabled={isSavingRegion}
                        className="mt-2 w-full rounded-xl py-2 text-sm font-semibold text-white transition"
                        style={{
                          backgroundColor: SIDEBAR_COLORS.amethyst,
                          opacity: isSavingRegion ? 0.7 : 1,
                        }}
                      >
                        {isSavingRegion ? "Saving..." : "Save Region"}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {!profile.canLogout ? (
                <button
                  onClick={() => navigate("/login")}
                  className="flex-1 rounded-2xl px-4 py-2 text-sm font-semibold border transition"
                  style={{
                    borderColor: `${SIDEBAR_COLORS.roseGold}55`,
                    color: SIDEBAR_COLORS.darkAmethyst,
                    backgroundColor: "white",
                  }}
                >
                  Login
                </button>
              ) : (
                <>
                  {!isEditingProfile ? (
                    <div className="grid grid-cols-2 gap-2">
                      {isTester ? (
                        <div
                          className="rounded-2xl px-4 py-2 text-sm font-semibold border text-center"
                          style={{
                            borderColor: `${SIDEBAR_COLORS.roseGold}55`,
                            color: SIDEBAR_COLORS.darkAmethyst,
                            backgroundColor: `${SIDEBAR_COLORS.roseGold}22`,
                          }}
                        >
                          🔒 Profile Locked
                        </div>
                      ) : (
                        <button
                          onClick={() => setIsEditingProfile(true)}
                          className="rounded-2xl px-4 py-2 text-sm font-semibold border transition"
                          style={{
                            borderColor: `${SIDEBAR_COLORS.roseGold}55`,
                            color: SIDEBAR_COLORS.darkAmethyst,
                            backgroundColor: "white",
                          }}
                        >
                          Edit
                        </button>
                      )}

                      <button
                        onClick={handleLogout}
                        className="rounded-2xl px-4 py-2 text-sm font-semibold border transition flex items-center justify-center gap-2"
                        style={{
                          borderColor: `${SIDEBAR_COLORS.roseGold}55`,
                          color: SIDEBAR_COLORS.roseGold,
                          backgroundColor: SIDEBAR_COLORS.amethyst,
                        }}
                        disabled={isSavingProfile}
                      >
                        <LogOut size={16} />
                        Logout
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => {
                            setIsEditingProfile(false);
                            setProfileForm({
                              username: profile.username || "",
                              email: profile.email || "",
                              mobileNo: profile.mobileNo || "",
                              dob: profile.dob || "",
                            });
                          }}
                          className="flex-1 rounded-2xl px-4 py-2 text-sm font-semibold border transition"
                          style={{
                            borderColor: `${SIDEBAR_COLORS.roseGold}55`,
                            color: SIDEBAR_COLORS.darkAmethyst,
                            backgroundColor: "white",
                          }}
                          disabled={isSavingProfile}
                        >
                          Cancel
                        </button>

                        <button
                          onClick={handleSaveProfile}
                          className="rounded-2xl px-4 py-2 text-sm font-semibold border transition"
                          style={{
                            borderColor: `${SIDEBAR_COLORS.roseGold}55`,
                            color: SIDEBAR_COLORS.roseGold,
                            backgroundColor: SIDEBAR_COLORS.amethyst,
                            opacity: isSavingProfile ? 0.7 : 1,
                          }}
                          disabled={isSavingProfile}
                        >
                          {isSavingProfile ? "Saving..." : "Save"}
                        </button>
                      </div>

                      <button
                        onClick={handleLogout}
                        className="w-full rounded-2xl px-4 py-2 text-sm font-semibold border transition flex items-center justify-center gap-2"
                        style={{
                          borderColor: `${SIDEBAR_COLORS.roseGold}55`,
                          color: SIDEBAR_COLORS.roseGold,
                          backgroundColor: SIDEBAR_COLORS.amethyst,
                        }}
                        disabled={isSavingProfile}
                      >
                        <LogOut size={16} />
                        Logout
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        className="
  w-[92vw]
  max-w-[520px]
  flex
  items-center
  justify-between
  gap-1
  rounded-3xl
  backdrop-blur-lg
  border
  shadow-lg
  px-2 py-2
"
        style={{
          backgroundColor: SIDEBAR_COLORS.pearl,
          borderColor: `${SIDEBAR_COLORS.roseGold}40`,
        }}
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = pathname === item.to;

          const handleNavClick = () => {
            if (item.to === "/sanctuary" && isTester) {
              toast.error("You do not have access");
              return;
            }
            navigate(item.to);
          };

          return (
            <button
              key={item.label}
              onClick={handleNavClick}
              className="flex flex-1 min-w-0 flex-col items-center gap-0.5 px-1 py-1 rounded-xl transition-all"
              style={{
                backgroundColor: active
                  ? SIDEBAR_COLORS.amethyst
                  : "transparent",
                color: active
                  ? SIDEBAR_COLORS.roseGold
                  : SIDEBAR_COLORS.darkAmethyst,
              }}
            >
              <Icon size={16} />
              <span className="text-[9px] font-medium truncate w-full text-center">
                {t(item.label)}
              </span>
            </button>
          );
        })}

        {profile.canLogout ? (
          <button
            onClick={() => setIsProfileOpen(true)}
            className="flex flex-1 min-w-0 flex-col items-center gap-0.5 px-1 py-1 rounded-xl transition-all"
            style={{
              backgroundColor: isProfileOpen
                ? SIDEBAR_COLORS.amethyst
                : "transparent",
              color: isProfileOpen
                ? SIDEBAR_COLORS.roseGold
                : SIDEBAR_COLORS.darkAmethyst,
            }}
            aria-label="Open profile"
          >
            <User size={16} />
            <span className="text-[9px] font-medium truncate w-full text-center">
              {t("astriaHome.nav.profile")}
            </span>
          </button>
        ) : null}
      </div>
    </>
  );
}
